import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import { cn, formatDate, formatCurrency } from '@/lib/utils.ts'
import { useSapItemSearch } from '@/hooks/useSapItemSearch'
import { useState, useRef, useCallback } from 'react'
import {
  ArrowLeft, Save, Plus, X, Loader2, AlertCircle,
  ShoppingCart, GripVertical,
} from 'lucide-react'

// ─── Types ──────────────────────────────────────────────────────────────────

type LineType = 'article' | 'text' | 'subtotal'

interface SapDocumentLine {
  lineNum: number
  lineType: LineType
  itemCode: string
  description: string
  freeText: string
  quantity: number
  price: number
  purchasePrice: number
  margin: number
  discount: number
  lineTotal: number
  parentLine: number | null
  isBomParent: boolean
}

interface SapDocument {
  sapDocEntry: number
  sapDocNum: number
  date: string
  customerCode: string
  customerName: string
  grossTotal: number
  netTotal: number
  currency: string
  status: string
  notes?: string
  ticketId?: string | null
  lines: SapDocumentLine[]
}

interface EditorLine {
  lineType: LineType
  itemCode: string
  description: string
  freeText: string
  quantity: number
  unitPrice: number
  purchasePrice: number
  margin: number
  parentLine: number | null
}

function emptyArticle(): EditorLine {
  return { lineType: 'article', itemCode: '', description: '', freeText: '', quantity: 1, unitPrice: 0, purchasePrice: 0, margin: 0, parentLine: null }
}
function emptyText(): EditorLine {
  return { lineType: 'text', itemCode: '', description: '', freeText: '', quantity: 0, unitPrice: 0, purchasePrice: 0, margin: 0, parentLine: null }
}
function emptySubtotal(): EditorLine {
  return { lineType: 'subtotal', itemCode: '', description: 'Zwischensumme', freeText: '', quantity: 0, unitPrice: 0, purchasePrice: 0, margin: 0, parentLine: null }
}

function docToEditorLines(doc: SapDocument): EditorLine[] {
  return doc.lines.map((l) => ({
    lineType: l.lineType,
    itemCode: l.itemCode,
    description: l.description,
    freeText: l.freeText ?? '',
    quantity: l.quantity,
    unitPrice: l.price,
    purchasePrice: l.purchasePrice ?? 0,
    margin: l.margin ?? 0,
    parentLine: l.parentLine,
  }))
}

function linesToPayload(lines: EditorLine[]) {
  return lines.filter((l) => l.lineType !== 'article' || l.itemCode).map((l) => ({
    lineType: l.lineType,
    itemCode: l.itemCode || undefined,
    description: l.description || undefined,
    freeText: l.freeText || undefined,
    quantity: l.lineType === 'article' ? l.quantity : undefined,
    unitPrice: l.lineType === 'article' ? l.unitPrice : undefined,
    parentLine: l.parentLine,
  }))
}

// ─── Inline add buttons between rows ────────────────────────────────────────

function InlineAddButtons({ onAdd }: { onAdd: (type: LineType) => void }) {
  const [visible, setVisible] = useState(false)
  return (
    <div
      className="relative h-2 group"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <div className={cn(
        'absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center gap-2 transition-opacity duration-150',
        visible ? 'opacity-100' : 'opacity-0',
      )}>
        <div className="h-px flex-1 bg-gray-200" />
        <button type="button" onClick={() => onAdd('article')} className="text-[10px] text-gray-400 hover:text-brand-600 px-1.5 py-0.5 rounded hover:bg-gray-100 whitespace-nowrap">+ Artikel</button>
        <button type="button" onClick={() => onAdd('text')} className="text-[10px] text-gray-400 hover:text-blue-600 px-1.5 py-0.5 rounded hover:bg-gray-100 whitespace-nowrap">+ Text</button>
        <button type="button" onClick={() => onAdd('subtotal')} className="text-[10px] text-gray-400 hover:text-amber-600 px-1.5 py-0.5 rounded hover:bg-gray-100 whitespace-nowrap">+ Summe</button>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
    </div>
  )
}

// ─── Shared Quotation View/Edit Content ─────────────────────────────────────

function QuotationViewContent({ ticketId, doc, onClose, isModal }: {
  ticketId: string
  doc: SapDocument
  onClose?: () => void
  isModal?: boolean
}) {
  const qc = useQueryClient()
  const isOpen = doc.status === 'open'

  // Editing
  const [editing, setEditing] = useState(false)
  const [comments, setComments] = useState('')
  const [lines, setLines] = useState<EditorLine[]>([])

  const startEditing = () => {
    setComments(doc.notes ?? '')
    setLines(docToEditorLines(doc))
    setEditing(true)
  }

  // Auto-enter edit mode on double-click (only if open)
  const handleRowDoubleClick = useCallback(() => {
    if (!editing && isOpen) startEditing()
  }, [editing, isOpen])

  // Item search
  const [itemSearch, setItemSearch] = useState('')
  const [searchingLineIdx, setSearchingLineIdx] = useState<number | null>(null)
  const { data: items = [], isFetching: itemsFetching } = useSapItemSearch(itemSearch)

  // Save
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.patch(`/tickets/${ticketId}/quotations/${doc.sapDocEntry}`, payload).then((r) => r.data),
    onSuccess: () => {
      setEditing(false)
      qc.invalidateQueries({ queryKey: ['tickets', ticketId, 'linked-documents'] })
    },
  })

  const handleSave = () => {
    saveMutation.mutate({ comments: comments || undefined, lines: linesToPayload(lines) })
  }

  // Line helpers — auto-enter editing if needed
  const ensureEditing = (): EditorLine[] => {
    if (!editing) {
      const editorLines = docToEditorLines(doc)
      setComments(doc.notes ?? '')
      setLines(editorLines)
      setEditing(true)
      return editorLines
    }
    return lines
  }
  const insertLine = (afterIdx: number, type: LineType) => {
    const line = type === 'text' ? emptyText() : type === 'subtotal' ? emptySubtotal() : emptyArticle()
    const current = ensureEditing()
    setLines([...current.slice(0, afterIdx + 1), line, ...current.slice(afterIdx + 1)])
  }
  const addLineAtEnd = (type: LineType) => {
    const line = type === 'text' ? emptyText() : type === 'subtotal' ? emptySubtotal() : emptyArticle()
    const current = ensureEditing()
    setLines([...current, line])
  }
  const removeLine = (idx: number) => setLines((l) => l.filter((_, i) => i !== idx))
  const updateLine = (idx: number, updates: Partial<EditorLine>) =>
    setLines((l) => l.map((line, i) => (i === idx ? { ...line, ...updates } : line)))

  // Drag & drop
  const dragIdx = useRef<number | null>(null)
  const handleDragStart = (idx: number) => { dragIdx.current = idx }
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }
  const handleDrop = (targetIdx: number) => {
    const from = dragIdx.current
    if (from === null || from === targetIdx) return
    setLines((prev) => {
      const arr = [...prev]
      const [moved] = arr.splice(from, 1)
      arr.splice(targetIdx, 0, moved!)
      return arr
    })
    dragIdx.current = null
  }

  const total = editing
    ? lines.reduce((s, l) => l.lineType === 'article' ? s + l.quantity * l.unitPrice : s, 0)
    : doc.netTotal

  return (
    <div className={cn(
      'bg-white overflow-hidden',
      isModal ? 'flex flex-col h-full rounded-xl' : 'rounded-xl border border-gray-200 shadow-sm',
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-bold text-gray-900">Angebot #{doc.sapDocNum}</h1>
          <span className={cn(
            'text-xs font-medium px-2 py-0.5 rounded',
            isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500',
          )}>
            {isOpen ? 'Offen' : 'Geschlossen'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg">
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="px-4 py-1.5 text-sm font-medium bg-brand-500 hover:bg-brand-600 text-white rounded-lg flex items-center gap-1.5"
              >
                {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Speichern
              </button>
            </>
          ) : (
            <>
              {!isOpen && (
                <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mr-2">
                  Geschlossen — Doppelklick nicht möglich
                </span>
              )}
              {isOpen && (
                <span className="text-xs text-gray-400 mr-2">Doppelklick zum Bearbeiten</span>
              )}
            </>
          )}
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-1">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-4 border-b border-gray-100 text-sm">
        <div>
          <span className="text-xs text-gray-400 block">Angebotsnr.</span>
          <span className="font-mono font-medium text-gray-900">{doc.sapDocNum}</span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Datum</span>
          <span className="text-gray-900">{formatDate(doc.date)}</span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Kunde</span>
          <span className="text-gray-900">{doc.customerName}</span>
          <span className="text-xs text-gray-400 font-mono ml-1">{doc.customerCode}</span>
        </div>
        <div>
          <span className="text-xs text-gray-400 block">Ticket</span>
          <span className="text-gray-900">#{ticketId}</span>
        </div>
      </div>

      {/* Bemerkung */}
      {(editing || doc.notes) && (
        <div className="px-6 py-3 border-b border-gray-100" onDoubleClick={handleRowDoubleClick}>
          <span className="text-xs text-gray-400 block mb-1">Bemerkung</span>
          {editing ? (
            <input
              type="text"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          ) : (
            <p className="text-sm text-gray-700">{doc.notes}</p>
          )}
        </div>
      )}

      {/* Error */}
      {saveMutation.error && (
        <div className="mx-6 mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          Fehler: {saveMutation.error instanceof Error ? saveMutation.error.message : 'Unbekannter Fehler'}
        </div>
      )}

      {/* ─── Positionen ─── */}
      <div className={cn('px-6 py-4', isModal && 'flex-1 overflow-y-auto')}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Positionen</span>
          {isOpen && (
            <div className="flex gap-2">
              <button type="button" onClick={() => addLineAtEnd('article')} className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Artikel
              </button>
              <button type="button" onClick={() => addLineAtEnd('text')} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Text
              </button>
              <button type="button" onClick={() => addLineAtEnd('subtotal')} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Summe
              </button>
            </div>
          )}
        </div>

        {/* Column headers — SAP-style: Pos | Qty | ItemNo. / Description | Price € | Total € */}
        <div className="grid grid-cols-[40px_50px_1fr_90px_90px] gap-0 text-[11px] font-semibold text-gray-600 bg-brand-600 text-white py-1.5 px-2 rounded-t">
          <span>Pos</span>
          <span>Menge</span>
          <span>ArtikelNr. / Beschreibung</span>
          <span className="text-right">Preis &euro;</span>
          <span className="text-right">Gesamt &euro;</span>
        </div>

        {/* Lines */}
        <div className="border-x border-gray-200">
          {(editing ? lines : doc.lines).map((line, idx) => {
            const lt = line.lineType ?? 'article'

            return (
              <div key={idx}>
                {/* Inline add buttons between rows */}
                {isOpen && idx > 0 && (
                  <InlineAddButtons onAdd={(type) => insertLine(idx - 1, type)} />
                )}

                {/* The row */}
                <div
                  draggable={editing}
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(idx)}
                  onDoubleClick={handleRowDoubleClick}
                  className={cn(
                    'border-b border-gray-100 transition-colors',
                    editing && 'cursor-grab active:cursor-grabbing',
                    !editing && isOpen && 'cursor-pointer hover:bg-gray-50/50',
                  )}
                >
                  {lt === 'text' ? (
                    // ─── Text line ───
                    <div className="grid grid-cols-[40px_50px_1fr_90px_90px] gap-0 py-2 px-2 bg-blue-50/30">
                      <span />
                      <span />
                      <div className="flex items-start gap-1.5">
                        {editing && <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0 mt-0.5" />}
                        {editing ? (
                          <input
                            type="text"
                            value={(line as EditorLine).description}
                            onChange={(e) => updateLine(idx, { description: e.target.value })}
                            placeholder="Freitext..."
                            className="flex-1 border border-gray-300 rounded px-2 py-0.5 text-sm focus:ring-1 focus:ring-brand-500"
                          />
                        ) : (
                          <span className="text-sm text-gray-700 italic">{(line as SapDocumentLine).description}</span>
                        )}
                        {editing && (
                          <button type="button" onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-500 shrink-0 mt-0.5">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <span />
                      <span />
                    </div>

                  ) : lt === 'subtotal' ? (
                    // ─── Subtotal line ───
                    (() => {
                      const subtotal = editing
                        ? lines.slice(0, idx).reduce((s, l) => {
                            if (l.lineType === 'subtotal') return 0
                            if (l.lineType === 'article') return s + l.quantity * l.unitPrice
                            return s
                          }, 0)
                        : (line as SapDocumentLine).lineTotal
                      return (
                        <div className="grid grid-cols-[40px_50px_1fr_90px_90px] gap-0 py-2 px-2 bg-amber-50/40 border-t border-amber-300">
                          <span />
                          <span />
                          <div className="flex items-center gap-1.5">
                            {editing && <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                            {editing ? (
                              <input
                                type="text"
                                value={(line as EditorLine).description}
                                onChange={(e) => updateLine(idx, { description: e.target.value })}
                                className="flex-1 border border-gray-300 rounded px-2 py-0.5 text-sm font-semibold focus:ring-1 focus:ring-brand-500"
                              />
                            ) : (
                              <span className="text-sm font-semibold text-gray-700">{(line as SapDocumentLine).description}</span>
                            )}
                            {editing && (
                              <button type="button" onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-500 shrink-0">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <span />
                          <span className="text-right text-sm font-semibold text-gray-900 tabular-nums">{formatCurrency(subtotal)}</span>
                        </div>
                      )
                    })()

                  ) : (() => {
                    // ─── Article line ───
                    const isEditorLine = editing
                    const eLine = isEditorLine ? (line as EditorLine) : null
                    const rLine = !isEditorLine ? (line as SapDocumentLine) : null

                    const itemCode = eLine?.itemCode ?? rLine?.itemCode ?? ''
                    const description = eLine?.description ?? rLine?.description ?? ''
                    const freeText = eLine?.freeText ?? rLine?.freeText ?? ''
                    const quantity = eLine?.quantity ?? rLine?.quantity ?? 0
                    const unitPrice = eLine?.unitPrice ?? rLine?.price ?? 0
                    const lineTotal = isEditorLine ? quantity * unitPrice : (rLine?.lineTotal ?? 0)
                    const parentLine = eLine?.parentLine ?? rLine?.parentLine

                    return (
                      <div className={cn('py-2 px-2', parentLine != null && 'ml-4 bg-gray-50/50')}>
                        {/* Main row: Pos | Qty | ItemNo. Description | Price | Total */}
                        <div className="grid grid-cols-[40px_50px_1fr_90px_90px] gap-0 items-start">
                          {/* Pos */}
                          <span className="text-sm text-gray-400 tabular-nums">{idx + 1}</span>

                          {/* Qty */}
                          {editing ? (
                            <input type="number" min={1} value={quantity}
                              onChange={(e) => updateLine(idx, { quantity: Math.max(1, Number(e.target.value)) })}
                              className="w-12 border border-gray-300 rounded px-1 py-0.5 text-sm text-center" />
                          ) : (
                            <span className="text-sm text-gray-700">{quantity}</span>
                          )}

                          {/* ItemNo. / Description */}
                          <div className="pr-3">
                            <div className="flex items-start gap-1.5">
                              {editing && <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0 mt-0.5 cursor-grab" />}
                              <div className="flex-1 relative">
                                {editing ? (
                                  <>
                                    {/* ArtikelNr. = Suchfeld */}
                                    <div className="relative mb-0.5">
                                      <input
                                        type="text"
                                        value={itemCode}
                                        onChange={(e) => {
                                          updateLine(idx, { itemCode: e.target.value, description: '', unitPrice: 0, purchasePrice: 0, margin: 0 })
                                          setItemSearch(e.target.value)
                                          setSearchingLineIdx(idx)
                                        }}
                                        placeholder="Artikelnummer suchen..."
                                        className="w-48 border border-gray-300 rounded px-2 py-0.5 text-sm font-mono focus:ring-1 focus:ring-brand-500"
                                      />
                                      {/* Search dropdown */}
                                      {searchingLineIdx === idx && itemSearch.length >= 2 && (
                                        <div className="absolute z-[60] top-full left-0 w-[400px] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                          {itemsFetching ? (
                                            <div className="px-3 py-3 text-sm text-gray-400 flex items-center gap-2">
                                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Suche in SAP...
                                            </div>
                                          ) : items.length === 0 ? (
                                            <div className="px-3 py-3 text-sm text-gray-400">
                                              Keine Artikel für &quot;{itemSearch}&quot;
                                            </div>
                                          ) : items.map((item) => (
                                            <button
                                              type="button"
                                              key={item.ItemCode}
                                              onClick={() => {
                                                updateLine(idx, {
                                                  itemCode: item.ItemCode,
                                                  description: item.ItemName,
                                                  unitPrice: item.SalesPrice || 0,
                                                  purchasePrice: item.PurchasePrice || 0,
                                                  margin: item.SalesPrice && item.PurchasePrice
                                                    ? Number((((item.SalesPrice - item.PurchasePrice) / item.SalesPrice) * 100).toFixed(1))
                                                    : 0,
                                                })
                                                setSearchingLineIdx(null)
                                              }}
                                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                                            >
                                              <span className="font-mono text-[10px] text-gray-400 shrink-0">{item.ItemCode}</span>
                                              <span className="flex-1 truncate">{item.ItemName}</span>
                                              <span className="text-xs text-gray-400 shrink-0">{formatCurrency(item.SalesPrice)}</span>
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    {/* Description — editable, populated when an item is selected */}
                                    <input
                                      type="text"
                                      value={description}
                                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                                      placeholder="Beschreibung..."
                                      className="w-full border border-gray-300 rounded px-2 py-0.5 text-sm font-medium focus:ring-1 focus:ring-brand-500"
                                    />
                                  </>
                                ) : (
                                  <span className="text-sm">
                                    <span className="font-mono text-[11px] text-gray-400 mr-1.5">{itemCode}</span>
                                    <span className="font-medium text-gray-900">{description}</span>
                                  </span>
                                )}
                              </div>
                              {editing && (
                                <button type="button" onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-500 shrink-0 mt-0.5">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>

                            {/* Line extension — multiline text below description */}
                            {editing ? (
                              <textarea
                                value={eLine?.freeText ?? ''}
                                onChange={(e) => updateLine(idx, { freeText: e.target.value })}
                                placeholder="Zeilenerweiterung (mehrzeiliger Zusatztext)..."
                                rows={freeText ? Math.max(2, freeText.split('\n').length) : 1}
                                className="w-full mt-1 ml-5 border border-dashed border-gray-300 rounded px-2 py-1 text-xs text-gray-600 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 placeholder:text-gray-300 resize-y"
                              />
                            ) : freeText ? (
                              <div className="mt-1 ml-0 text-xs text-gray-500 whitespace-pre-wrap leading-relaxed">
                                {freeText}
                              </div>
                            ) : null}

                            {/* Parent selector */}
                            {editing && itemCode && (
                              <div className="ml-5 mt-1 flex items-center gap-2">
                                <label className="text-[10px] text-gray-400">Übergeordnet:</label>
                                <select value={eLine?.parentLine ?? ''}
                                  onChange={(e) => updateLine(idx, { parentLine: e.target.value ? Number(e.target.value) : null })}
                                  className="border border-gray-200 rounded text-[10px] text-gray-500 px-1 py-0.5 bg-white">
                                  <option value="">— Keine</option>
                                  {lines.map((other, oi) =>
                                    other.lineType === 'article' && other.itemCode && oi !== idx && other.parentLine == null
                                      ? <option key={oi} value={oi}>#{oi + 1} {other.description || other.itemCode}</option>
                                      : null
                                  )}
                                </select>
                              </div>
                            )}
                          </div>

                          {/* Preis € */}
                          {editing ? (
                            <input type="number" min={0} step={0.01} value={unitPrice}
                              onChange={(e) => {
                                const vk = Number(e.target.value)
                                const margin = vk > 0 && (eLine?.purchasePrice ?? 0) > 0
                                  ? Number((((vk - (eLine?.purchasePrice ?? 0)) / vk) * 100).toFixed(1)) : 0
                                updateLine(idx, { unitPrice: vk, margin })
                              }}
                              className="w-20 border border-gray-300 rounded px-1 py-0.5 text-sm text-right" />
                          ) : (
                            <span className="text-right text-sm text-gray-700 tabular-nums">{formatCurrency(unitPrice)}</span>
                          )}

                          {/* Gesamt € */}
                          <span className="text-right text-sm font-medium text-gray-900 tabular-nums">{formatCurrency(lineTotal)}</span>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              </div>
            )
          })}

          {/* Inline add at end */}
          {isOpen && <InlineAddButtons onAdd={(type) => addLineAtEnd(type)} />}
        </div>

        {/* Totals — right-aligned like SAP */}
        <div className="border-x border-b border-gray-200 rounded-b">
          <div className="grid grid-cols-[1fr_90px_90px] gap-0 px-2 py-2 border-t-2 border-gray-300">
            <span className="text-right text-sm text-gray-500 pr-4">Netto</span>
            <span />
            <span className="text-right text-sm font-medium text-gray-900 tabular-nums">{formatCurrency(total)}</span>
          </div>
          <div className="grid grid-cols-[1fr_90px_90px] gap-0 px-2 py-1">
            <span className="text-right text-sm text-gray-500 pr-4">MwSt. 19%</span>
            <span />
            <span className="text-right text-sm text-gray-600 tabular-nums">{formatCurrency((editing ? total * 0.19 : doc.grossTotal - doc.netTotal))}</span>
          </div>
          <div className="grid grid-cols-[1fr_90px_90px] gap-0 px-2 py-2 border-t border-gray-200 bg-gray-50">
            <span className="text-right text-sm font-semibold text-gray-700 pr-4">Brutto</span>
            <span />
            <span className="text-right text-sm font-bold text-gray-900 tabular-nums">{formatCurrency(editing ? total * 1.19 : doc.grossTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Modal Wrapper ──────────────────────────────────────────────────────────

export function QuotationViewModal({ ticketId, doc, onClose }: {
  ticketId: string
  doc: SapDocument
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="w-full max-w-4xl h-[80vh] flex flex-col rounded-xl bg-white border border-gray-200 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <QuotationViewContent ticketId={ticketId} doc={doc} onClose={onClose} isModal />
      </div>
    </div>
  )
}

// ─── Page Wrapper ───────────────────────────────────────────────────────────

export function QuotationViewPage() {
  const { ticketId, docEntry } = useParams<{ ticketId: string; docEntry: string }>()
  const navigate = useNavigate()

  const { data: linkedDocs, isLoading } = useQuery<{ quotations: SapDocument[]; orders: SapDocument[] }>({
    queryKey: ['tickets', ticketId, 'linked-documents'],
    queryFn: () => apiClient.get(`/tickets/${ticketId}/linked-documents`).then((r) => r.data),
    enabled: !!ticketId,
    staleTime: 5 * 60_000,
  })

  const doc = linkedDocs?.quotations?.find((q) => q.sapDocEntry === Number(docEntry))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <button onClick={() => navigate(`/tickets/${ticketId}`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Zurück zum Ticket
        </button>
        <div className="text-center py-20">
          <AlertCircle className="w-8 h-8 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">Angebot nicht gefunden</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button onClick={() => navigate(`/tickets/${ticketId}`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Zurück zum Ticket #{ticketId}
      </button>
      <QuotationViewContent ticketId={ticketId!} doc={doc} />
    </div>
  )
}
